import React, { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import ListingService from '../services/ListingService';

const getAreaCounts = (area, listings) => {
  if (area && area.forSaleCount !== undefined && area.forSaleCount !== null && area.forRentCount !== undefined && area.forRentCount !== null) {
    return { sale: area.forSaleCount, rent: area.forRentCount };
  }

  const title = (area?.title || '').trim().toLowerCase();
  const slug = title.replace(/\s+/g, '-');
  
  let sale = 0;
  let rent = 0;

  if (listings && Array.isArray(listings)) {
    listings.forEach((l) => {
      if (!l || !l.area) return;
      const lArea = l.area.trim().toLowerCase();
      const lSlug = lArea.replace(/\s+/g, '-');
      if (lArea === title || lSlug === slug || lArea.includes(title) || title.includes(lArea)) {
        const type = (l.listingType || '').toUpperCase();
        if (type.includes('RENT')) {
          rent++;
        } else {
          sale++;
        }
      }
    });
  }

  return { sale, rent };
};

const PopularAreas = ({ areas, listings, onSelectArea }) => {
  const navigate = useNavigate();
  const sliderRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [activeDot, setActiveDot] = useState(0);

  const handleAreaClick = (area) => {
    const slug = area.title.toLowerCase().replace(/\s+/g, '-');
    navigate(`/area/${slug}`);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const checkScroll = () => {
    if (sliderRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
      setCanScrollLeft(scrollLeft > 10);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 10);
      
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll > 0) {
        const pageRatio = scrollLeft / maxScroll;
        const totalDots = areas && areas.length > 4 ? Math.ceil(areas.length - 3) : 1;
        const currentDot = Math.min(Math.round(pageRatio * (totalDots - 1)), totalDots - 1);
        setActiveDot(Math.max(0, currentDot));
      }
    }
  };

  useEffect(() => {
    const el = sliderRef.current;
    if (el) {
      el.addEventListener('scroll', checkScroll);
      window.addEventListener('resize', checkScroll);
      checkScroll();
      const timer = setTimeout(checkScroll, 300);
      return () => {
        el.removeEventListener('scroll', checkScroll);
        window.removeEventListener('resize', checkScroll);
        clearTimeout(timer);
      };
    }
  }, [areas]);

  const scroll = (direction) => {
    if (sliderRef.current) {
      const { clientWidth } = sliderRef.current;
      const scrollAmount = direction === 'left' ? -clientWidth * 0.75 : clientWidth * 0.75;
      sliderRef.current.scrollBy({ left: scrollAmount, behavior: 'smooth' });
    }
  };

  const totalAreas = areas ? areas.length : 0;

  return (
    <section className="section-wrapper" id="areas">
      <div className="section-header areas-header">
        <div className="areas-header-left">
          <h2 className="section-title">POPULAR AREA PROPERTIES</h2>
        </div>
        
        <div className="areas-header-actions">
          <button 
            className="link-view-all btn-link-view-all"
            onClick={() => {
              navigate('/all-areas');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
          >
            VIEW ALL AREAS <i className="bi bi-arrow-right"></i>
          </button>
        </div>
      </div>

      <div className="areas-slider-wrapper">
        {canScrollLeft && (
          <button 
            className="areas-slider-overlay-btn prev"
            onClick={() => scroll('left')}
            aria-label="Previous areas"
          >
            <i className="bi bi-chevron-left"></i>
          </button>
        )}
        {canScrollRight && (
          <button 
            className="areas-slider-overlay-btn next"
            onClick={() => scroll('right')}
            aria-label="Next areas"
          >
            <i className="bi bi-chevron-right"></i>
          </button>
        )}

        <div className="areas-grid areas-slider-track" ref={sliderRef}>
          {areas && areas.map((area) => {
            const counts = getAreaCounts(area, listings);

            return (
              <div 
                className="area-card" 
                key={area.id}
                onClick={() => handleAreaClick(area)}
                style={{ cursor: 'pointer' }}
              >
                <div className="area-image-wrapper">
                  <img src={ListingService.formatImageUrl(area.imageUrl)} alt={area.title} className="area-image" />
                  <div className="area-badges-overlay">
                    <span className="area-badge area-badge-sale">
                      <i className="bi bi-tag-fill"></i> {counts.sale} For Sale
                    </span>
                    <span className="area-badge area-badge-rent">
                      <i className="bi bi-key-fill"></i> {counts.rent} For Rent
                    </span>
                  </div>
                </div>
              <div className="area-card-body">
                <h3 className="area-title">{area.title}</h3>
                <ul className="area-highlights">
                  {area.highlights && area.highlights.map((highlight, idx) => (
                    <li key={idx}>
                      <i className="bi bi-check-circle-fill"></i>
                      {highlight}
                    </li>
                  ))}
                </ul>
                <button 
                  className="btn-area-view"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleAreaClick(area);
                  }}
                >
                  VIEW PROPERTIES
                </button>
              </div>
            </div>
          );
        })}
        </div>
      </div>

      {totalAreas > 4 && (
        <div className="areas-slider-footer">
          <div className="areas-dots-pagination">
            {Array.from({ length: Math.max(1, totalAreas - 3) }).map((_, idx) => (
              <span 
                key={idx} 
                className={`areas-dot ${activeDot === idx ? 'active' : ''}`}
                onClick={() => {
                  if (sliderRef.current) {
                    const { scrollWidth, clientWidth } = sliderRef.current;
                    const maxScroll = scrollWidth - clientWidth;
                    const targetScroll = (idx / Math.max(1, totalAreas - 4)) * maxScroll;
                    sliderRef.current.scrollTo({ left: targetScroll, behavior: 'smooth' });
                  }
                }}
              ></span>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default PopularAreas;
